import type { WalletGroupedAccounts } from '@polaris/core/models'
import { formatPrice, getWalletColor, hexToRgba } from '@polaris/core/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import { Binoculars, Smartphone } from 'lucide-react'
import { type HTMLMotionProps, motion } from 'motion/react'
import Image from 'next/image'
import { cn } from '@/src/utils/styles'
import { Wallet } from '../ui/icons/wallet'
import { Skeleton } from '../ui/skeleton'

// Note called "WalletCard" in Figma
// https://www.figma.com/design/0aDCMLbosIK5nBcSwV196E/%E2%9C%B4%EF%B8%8F-Pattern-Library?node-id=1025-7169&t=Z4eGo9U1V2L5ZvxH-4

// These affect opacity of the wallet custom color background
const WALLET_BG_OPACITY = 0.6
const WALLET_BG_HOVER_OPACITY = 0.5

const accountCardVariants = cva(
  'z-10 aspect-[42/23] rounded-2xl overflow-hidden light bg-background bg-gradient-to-br from-10% from-surface3 via-50% via-foreground to-70% to-surface2 flex transition-shadow',
  {
    variants: {
      size: {
        // Current behavior: flexible between 168px and 248px width
        md: 'min-w-[10.5rem] max-w-[15.5rem]',
        // Screenshot size: fixed 248px width (height derived from aspect ratio)
        lg: 'w-[15.5rem]',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
)

interface AccountCardBaseProps {
  account: WalletGroupedAccounts
  textContentClassNames?: string
}

interface AccountCardContentsProps extends AccountCardBaseProps {
  isLoading?: boolean
  /** Override the total value in the card. */
  totalValueReplaceContent?: string
}

interface AccountCardButtonProps
  extends AccountCardBaseProps,
    HTMLMotionProps<'button'>,
    VariantProps<typeof accountCardVariants> {}

export const AccountCardButton = ({
  account,
  className,
  size,
  style,
  ...props
}: AccountCardButtonProps) => {
  const color = getWalletColor(account.provider_info.name, account.type)

  return (
    <motion.button
      layoutId={account.grouping_key}
      key={account.grouping_key}
      layout
      transition={{
        duration: 0.4,
        type: 'spring',
        stiffness: 300,
        damping: 30,
      }}
      {...props}
      className={cn(accountCardVariants({ size }), className)}
      initial={{
        '--wallet-bg': hexToRgba(color, WALLET_BG_OPACITY),
      }}
      style={{
        originX: 0.5,
        originY: 0.5,
        ...style,
      }}
      whileHover={{
        y: -1,
        rotate: 2,
        '--wallet-bg': hexToRgba(color, WALLET_BG_HOVER_OPACITY),
      }}
    >
      <div
        className='p-3 pb-2 flex flex-col gap-2 w-full justify-between rounded-2xl'
        style={{
          backgroundColor: 'var(--wallet-bg)',
        }}
      >
        <AccountCardContents account={account} />
      </div>
    </motion.button>
  )
}

interface AccountCardProps
  extends AccountCardBaseProps,
    HTMLMotionProps<'div'>,
    VariantProps<typeof accountCardVariants> {
  isLoading?: boolean
  skeletonClassNames?: string
  textContentClassNames?: string
  /** Override the total value in the card. */
  totalValueReplaceContent?: string
}

export const AccountCard = ({
  account,
  className,
  isLoading,
  skeletonClassNames,
  textContentClassNames,
  totalValueReplaceContent,
  size,
  style,
  ...props
}: AccountCardProps) => {
  const color = getWalletColor(account.provider_info.name, account.type)

  return (
    <motion.div
      layoutId={account.grouping_key}
      key={account.grouping_key}
      layout
      transition={{
        duration: 0.4,
        type: 'spring',
        stiffness: 300,
        damping: 30,
      }}
      {...props}
      className={cn(accountCardVariants({ size }), className)}
      initial={{
        '--wallet-bg': hexToRgba(color, WALLET_BG_OPACITY),
      }}
      style={{
        originX: 0.5,
        originY: 0.5,
        ...style,
      }}
      whileHover={{
        rotate: -2,
        '--wallet-bg': hexToRgba(color, WALLET_BG_HOVER_OPACITY),
      }}
    >
      <div
        className='p-3 pb-2 flex flex-col gap-2 w-full justify-between rounded-2xl'
        style={{
          backgroundColor: 'var(--wallet-bg)',
        }}
      >
        <AccountCardContents
          textContentClassNames={textContentClassNames}
          account={account}
          isLoading={isLoading}
          totalValueReplaceContent={totalValueReplaceContent}
        />
      </div>
    </motion.div>
  )
}

const AccountCardContents = ({
  account,
  textContentClassNames,
  isLoading,
  totalValueReplaceContent,
}: AccountCardContentsProps) => {
  const { name, provider_info, totalValue, walletConnectionStatus } = account

  const isWatchAddress = account.type === 'WatchAddress'
  const isMobile = account.type === 'Mobile'

  return (
    <>
      {isWatchAddress ? (
        <span className='self-start flex items-center justify-center size-6'>
          <Binoculars size={24} className='text-green-800' />
        </span>
      ) : isMobile ? (
        <span className='self-start flex items-center justify-center size-6'>
          <Smartphone size={20} className='text-primary' />
        </span>
      ) : provider_info.img ? (
        <Image
          className={cn('self-start size-6 overflow-hidden rounded-md', {
            'opacity-50': walletConnectionStatus !== 'all',
          })}
          src={provider_info.img}
          alt={`${name} logo`}
          width={24}
          height={24}
          unoptimized
        />
      ) : (
        <span
          className={cn('self-start flex items-center justify-center size-6', {
            'opacity-50': walletConnectionStatus !== 'all',
          })}
        >
          <Wallet width={24} height={24} className='text-primary' />
        </span>
      )}
      <div
        className={cn(
          'flex flex-col text-sm tracking-tight text-left',
          textContentClassNames,
        )}
      >
        <div className='text-primary font-semibold text-ellipsis overflow-hidden whitespace-nowrap'>
          {name}
        </div>
        <div className='text-secondary h-5'>
          {isLoading ? (
            <Skeleton className='h-full w-20' />
          ) : totalValueReplaceContent !== undefined ? (
            totalValueReplaceContent
          ) : (
            formatPrice(totalValue)
          )}
        </div>
      </div>
    </>
  )
}
